import { colorTheme } from "@/themes/color";
import { alpha, createTheme, Shadows } from "@mui/material/styles";

import "@/themes/css/Montserrat.css";

declare module "@mui/material/styles" {
  interface Theme {
    surface: {
      lowest: string;
      low: string;
      normal: string;
      high: string;
      highest: string;
      on: string;
      onVariant: string;
    };
    model: {
      model: string;
      light: number;
    };
    inverse: {
      surface: string;
      contrastText: string;
    };
    outline: {
      main: string;
      focus: string;
      light: string;
    };
    progress: {
      shadows: {
        fill: string;
        empty: string;
      };
    };
  }

  interface ThemeOptions {
    surface?: {
      lowest?: string;
      low?: string;
      normal?: string;
      high?: string;
      highest?: string;
      on?: string;
      onVariant?: string;
    };
    model?: {
      model?: number;
      light?: number;
    };
    inverse?: {
      surface?: string;
      contrastText?: string;
    };
    outline?: {
      main?: string;
      focus?: string;
      light?: string;
    };
    progress?: {
      shadows?: {
        fill?: string;
        empty?: string;
      };
    };
  }
}

const defaultTheme = createTheme();

// Custom theme taken from https://asiga.atlassian.net/wiki/spaces/UiUX/pages/85917697/Design+Specs+for+UI+Components
export const theme = createTheme({
  palette: {
    primary: {
      main: colorTheme.primary.primary,
      contrastText: colorTheme.primary.onPrimary
    },
    secondary: {
      main: colorTheme.secondary.secondary,
      contrastText: colorTheme.secondary.onSecondary
    },
    info: {
      main: colorTheme.status.asigaBlue,
      light: colorTheme.status.asigaBlueContainer
    },
    success: {
      main: colorTheme.status.green,
      light: colorTheme.status.greenFill
    },
    warning: {
      main: colorTheme.status.amber,
      light: colorTheme.status.amberContainer
    },
    error: {
      main: colorTheme.status.error,
      contrastText: colorTheme.status.onError,
      light: colorTheme.status.errorContainer
    },
    background: {
      default: colorTheme.surfaces.surfaceContainerLowest,
      paper: colorTheme.surfaces.surfaceContainer
    },
    text: {
      primary: colorTheme.text.onSurface,
      secondary: colorTheme.text.onSurfaceVariant
    },
    action: {
      focus: colorTheme.status.focus
    }
  },
  surface: {
    lowest: colorTheme.surfaces.surfaceContainerLowest,
    low: colorTheme.surfaces.surfaceContainerLow,
    normal: colorTheme.surfaces.surfaceContainer,
    high: colorTheme.surfaces.surfaceContainerHigh,
    highest: colorTheme.surfaces.surfaceContainerHighest,
    on: colorTheme.text.onSurface,
    onVariant: colorTheme.text.onSurfaceVariant
  },
  model: {
    model: 0xffaa00,
    light: 0xcccccc
  },
  inverse: {
    surface: colorTheme.other.inverseSurface,
    contrastText: colorTheme.other.inverseOnSurface
  },
  outline: {
    main: colorTheme.other.outlineVariant,
    light: "#71787E"
  },
  progress: {
    shadows: {
      fill: `0px -4px 4px 0px inset ${alpha("#000000", 0.1)}, 0px 4px 4px 0px inset ${alpha("#FFFFFF", 0.1)}`, // progress bar fill
      empty: `0px -4px 4px 0px inset ${alpha("#FFFFFF", 0.1)}, 0px 4px 4px 0px inset ${alpha("#000000", 0.1)}` // progress bar empty
    }
  },
  shadows: [
    "none",
    `0px 1px 2px 0px ${alpha("#000000", 0.3)}, 0px 1px 3px 1px ${alpha("#000000", 0.15)}`, // level 1 shadow
    `0px 1px 2px 0px ${alpha("#000000", 0.3)}, 0px 2px 6px 2px ${alpha("#000000", 0.15)}`, // level 2 shadow
    ...defaultTheme.shadows.slice(3)
  ] as Shadows,

  typography: {
    fontFamily: "Montserrat, sans-serif",
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    h1: {
      fontSize: "1.5em",
      fontWeight: 500,
      width: "100%"
    },
    h2: {
      fontSize: "1.3em",
      width: "100%"
    },
    h4: {
      fontSize: "1.1em",
      width: "100%",
      color: "#20232499"
    },
    button: {
      textTransform: "none"
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: () => ({
          textTransform: "none",
          boxShadow: "none"
        })
      }
    },
    MuiTable: {
      styleOverrides: {
        root: ({ _, ownerState }) => ({
          ...(ownerState?.component === "table" && {
            borderRadius: "8px",
            borderCollapse: "separate"
          })
        })
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          ...(ownerState?.component === "td" && {
            padding: "0.5em 16px"
          }),
          "&.MuiTableCell-paddingCheckbox": {
            padding: "0.5em 0px",
            "&:first-of-type": {
              paddingLeft: "0.5em"
            },
            width: "42px",
            height: "42px"
          },
          "&.MuiTableCell-paddingNone": {
            padding: "0px 0px 0px 0.5em"
          },
          "&.MuiTableCell-stickyHeader": {
            backgroundColor: theme.surface.low
          },
          "& .MuiTypography-root": {
            fontSize: "1em !important"
          }
        })
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          whiteSpace: "pre-line",
          fontSize: "0.8rem"
        }
      }
    },
    MuiPopper: {
      styleOverrides: {
        root: () => ({
          "& .MuiPaper-root": {
            borderRadius: "4px",
            border: "2px solid #C1C7CE",
            color: "#202324"
          }
        })
      }
    },
    MuiPopover: {
      styleOverrides: {
        paper: () => ({
          borderRadius: "4px",
          border: "2px solid #C1C7CE",
          color: "#202324"
        })
      }
    }
  }
  // breakpoints: {
  //   values: {
  //     xs: 0,
  //     sm: 600,
  //     md: 840,
  //     lg: 1200,
  //     xl: 1536
  //   }
  // }
});
